<!--
  @component DatabaseCreateWizard — Phase 2 "New database" modal.
  Provision a managed Postgres on a host project, then link (share) it to any
  number of the owner's projects — the M:N create flow made concrete.
  reads:  useDeploymentIO (db.provision + db.connect), useIdentityIO (projects)
  steps:  Host → Share → Review
-->
<script lang="ts">
  import Wizard, { type WizardStepDef } from "../../../../global/visuals/primitives/Wizard.svelte";
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";

  interface Props {
    open: boolean;
    onClose: () => void;
    /** Called after a successful create so the aggregate can refresh. */
    onCreated?: () => void;
    /** Pre-select the host project (e.g. the single project in scope). */
    defaultHostId?: string | null;
  }
  const { open, onClose, onCreated, defaultHostId = null }: Props = $props();

  const identity = useIdentityIO();
  const node = useDeploymentIO();

  const STEPS: readonly WizardStepDef[] = [
    { id: "host", label: "Host" },
    { id: "share", label: "Share" },
    { id: "review", label: "Review" },
  ];

  let step = $state(0);
  let hostId = $state<string>("");
  let shareIds = $state<string[]>([]);
  let pending = $state(false);
  let error = $state<string | null>(null);
  let phase = $state<string>("");

  const projects = $derived(identity.projects);

  // Reset on open.
  $effect(() => {
    if (open) {
      step = 0;
      hostId = defaultHostId ?? (projects[0]?.id ?? "");
      shareIds = hostId ? [hostId] : [];
      pending = false; error = null; phase = "";
    }
  });
  // The host is always part of the share set.
  $effect(() => { if (hostId && !shareIds.includes(hostId)) shareIds = [hostId, ...shareIds]; });

  function nameOf(id: string): string { return projects.find((p) => p.id === id)?.name ?? id; }
  function toggleShare(id: string): void {
    if (id === hostId) return; // host is always linked
    shareIds = shareIds.includes(id) ? shareIds.filter((x) => x !== id) : [...shareIds, id];
  }

  const canAdvance = $derived.by(() => {
    if (pending) return false;
    if (step === 0) return hostId.length > 0;
    return true;
  });

  async function finish(): Promise<void> {
    if (!canAdvance || pending || !hostId) return;
    pending = true; error = null;
    try {
      phase = "Provisioning database…";
      const r = (await node.dispatch("db.provision", { target: { id: hostId } })) as { databaseId: string };
      const databaseId = r.databaseId;
      // Link the DSN into every selected project (host included) so release.run
      // injects DATABASE_URL on each one's next deploy.
      const targets = shareIds.includes(hostId) ? shareIds : [hostId, ...shareIds];
      for (const pid of targets) {
        phase = `Linking ${nameOf(pid)}…`;
        await node.dispatch("db.connect", { target: { id: pid }, databaseId });
      }
      onCreated?.();
      onClose();
    } catch (e) {
      error = e instanceof Error ? e.message : "could not create database";
    } finally {
      pending = false; phase = "";
    }
  }
</script>

<Wizard
  {open}
  title="New database"
  steps={STEPS}
  currentStep={step}
  {canAdvance}
  {pending}
  finishLabel="Create database"
  onStepChange={(n) => (step = n)}
  onFinish={() => void finish()}
  onCancel={onClose}
>
  {#snippet children(stepIndex: number)}
    {#if stepIndex === 0}
      <div class="step">
        <p class="lbl">Host project</p>
        <p class="hint">The database is provisioned on this project's infrastructure. You can share it with other projects next.</p>
        <div class="list">
          {#each projects as p (p.id)}
            <button type="button" class="opt" class:sel={hostId === p.id} disabled={pending} onclick={() => (hostId = p.id)}>
              <span class="radio" class:on={hostId === p.id}></span>
              <span class="opt-name">{p.name}</span>
            </button>
          {/each}
          {#if projects.length === 0}<p class="hint">No projects yet — create a project first.</p>{/if}
        </div>
      </div>
    {:else if stepIndex === 1}
      <div class="step">
        <p class="lbl">Share with projects</p>
        <p class="hint">Each linked project gets the connection string injected as <code>DATABASE_URL</code> on its next deploy. The host is always linked.</p>
        <div class="list">
          {#each projects as p (p.id)}
            <button type="button" class="opt" class:sel={shareIds.includes(p.id)} disabled={pending || p.id === hostId} onclick={() => toggleShare(p.id)}>
              <span class="check" class:on={shareIds.includes(p.id)}></span>
              <span class="opt-name">{p.name}</span>
              {#if p.id === hostId}<span class="tag">host</span>{/if}
            </button>
          {/each}
        </div>
      </div>
    {:else}
      <div class="step review">
        <dl>
          <div class="row"><dt>host</dt><dd><code>{nameOf(hostId)}</code></dd></div>
          <div class="row"><dt>engine</dt><dd><code>postgres-15</code></dd></div>
          <div class="row"><dt>shared with</dt><dd>{shareIds.map(nameOf).join(", ") || "—"}</dd></div>
        </dl>
        <p class="note"><strong>Next:</strong> we provision a managed Postgres on the host, then inject its <code>DATABASE_URL</code> into each linked project at deploy. Provisioning real infrastructure can take a minute.</p>
        {#if pending && phase}<p class="phase">{phase}</p>{/if}
        {#if error}<p class="error">{error}</p>{/if}
      </div>
    {/if}
  {/snippet}
</Wizard>

<style>
  .step { display: flex; flex-direction: column; gap: var(--space-2); }
  .lbl { margin: 0; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-tertiary); }
  .hint { margin: 0 0 var(--space-1); font-size: 11.5px; color: var(--text-tertiary); line-height: 1.5; }
  .hint code { font-family: var(--type-mono); font-size: 11px; }
  .list { display: flex; flex-direction: column; gap: 2px; max-height: 280px; overflow-y: auto; scrollbar-width: thin; }
  .opt {
    display: flex; align-items: center; gap: var(--space-2);
    background: transparent; border: 0.5px solid transparent; border-radius: var(--radius-md);
    padding: 9px var(--space-2); cursor: pointer; text-align: left; font: inherit; width: 100%;
    transition: background var(--ease-fast), border-color var(--ease-fast);
  }
  .opt:hover:not(:disabled) { background: var(--bg-hover); }
  .opt.sel { background: var(--bg-selected); border-color: var(--border-focus); }
  .opt:disabled { cursor: not-allowed; opacity: 0.6; }
  .opt.sel:disabled { opacity: 1; }
  .opt-name { flex: 1; min-width: 0; font-size: 13.5px; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .radio, .check { width: 15px; height: 15px; flex-shrink: 0; border: 1px solid var(--border-strong); background: var(--surface-sunken); }
  .radio { border-radius: var(--radius-pill); }
  .check { border-radius: 4px; }
  .radio.on { border-color: var(--accent, #5b8cff); box-shadow: inset 0 0 0 3px var(--accent, #5b8cff); }
  .check.on { border-color: var(--accent, #5b8cff); background: var(--accent, #5b8cff); }
  .tag { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; padding: 1px 6px; border-radius: var(--radius-pill); background: var(--bg-active); color: var(--text-tertiary); }

  .review dl { display: flex; flex-direction: column; gap: var(--space-2); margin: 0; }
  .row { display: flex; gap: var(--space-3); align-items: baseline; }
  dt { flex: 0 0 110px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-tertiary); }
  dd { margin: 0; font-size: 12.5px; color: var(--text-primary); }
  dd code { font-family: var(--type-mono); font-size: 12px; }
  .note { margin: var(--space-2) 0 0; padding: var(--space-3); background: var(--warning-subtle); border-radius: var(--radius-sm); font-size: 12px; color: var(--text-secondary); line-height: 1.55; }
  .note strong { color: var(--warning); font-weight: 500; }
  .note code { font-family: var(--type-mono); font-size: 11px; }
  .phase { margin: var(--space-2) 0 0; font-size: 12.5px; color: var(--text-secondary); }
  .error { margin: var(--space-2) 0 0; font-size: 12px; color: var(--error); font-family: var(--type-mono); }
</style>
